import axios from 'axios';
import { _transformContentResponse } from '../../services/content/models';
import { Endpoints } from './endpoints';
import { IFilter } from '../../util/url';
import { url } from './util';
import {
  IApplicationOptions,
  IChannel,
  ICustomer,
  IDestination,
  IPolicyApplication,
  IPostcodeAddress,
  IPostcodeLookup,
  ITrackingLink,
  TripType,
} from './models';
import { application, channels, customer, destinations, trackingLink } from './transformers';
import * as moment from 'moment';
import { get } from 'lodash';

export function listContents(filters?: IFilter[]) {
  return axios
    .get(url(Endpoints.CONTENT_LIST, { filters }))
    .then((response) => {
      return { data: response.data.data.map((res) => _transformContentResponse(res)) };
    });
}

export function getSignPosting(name, policyApplicationId) {
  return () => {
    const endpoint = 'public/contents/signpost';
    return axios
      .post(endpoint, {meta: {name, policy_application_id: policyApplicationId}},
        { headers: { 'Content-Type': 'application/vnd.api+json' } })
      .then((res: any) => {
        return res;
      });
  };
}

export function createPolicyApplication(policyApplication: IPolicyApplication,
                                        channelId: string,
                                        brokerId: string,
                                        options: IApplicationOptions = {}): Promise<IPolicyApplication> {
  const extraParams = {
    calculate: 'false',
  };

  if (options.calculate) {
    extraParams.calculate = 'true';
  }

  return axios
    .post(
      url(
        Endpoints.POLICY_APPLICATIONS,
        {
          params: { id: policyApplication.id, ...extraParams },
          includes: options.calculate ? ['premiums.scheme'] : [],
        },
      ),
      application.toJsonApi(policyApplication, channelId, brokerId),
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      const { data, included } = response.data;

      return application.fromJsonApi(data, included);
    });
}

export function updatePolicyApplication(policyApplication: IPolicyApplication,
                                        options: IApplicationOptions = {}): Promise<IPolicyApplication> {
  const extraParams = {
    calculate: 'false',
  };

  if (options.calculate) {
    extraParams.calculate = 'true';
  }

  return axios
    .put(
      url(
        Endpoints.POLICY_APPLICATION,
        {
          params: { id: policyApplication.id, ...extraParams },
          includes: options.calculate ? ['premiums.scheme', 'channel_scheme_documents'] : [],
        },
      ),
      application.toJsonApi(policyApplication),
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      const { data, included } = response.data;

      return application.fromJsonApi(data, included);
    });
}

export function saveQuote(policyApplication: IPolicyApplication,
                          channelId: string,
                          options: IApplicationOptions = {}): Promise<IPolicyApplication> {
  const extraParams = {
    calculate: 'false',
  };

  if (options.calculate) {
    extraParams.calculate = 'true';
  }

  return axios
    .post(
      url(
        Endpoints.POLICY_APPLICATIONS,
        {
          params: { id: policyApplication.id, ...extraParams },
          includes: options.calculate ? ['premiums.scheme', 'customer'] : [],
        },
      ),
      application.toJsonApi(policyApplication, channelId),
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      const { data, included } = response.data;

      return application.fromJsonApi(data, included);
    });
}

export function getTrackingLink(id: string): Promise<ITrackingLink> {
  return axios
    .get(url(Endpoints.TRACKING_LINKS, { params: { id } }))
    .then((response) => {
      return trackingLink.fromJsonApi(response.data.data, response.data.included);
    });
}

export function checkCampaignCode(code, chanelId: string) {
  return axios
    .get(
      url(Endpoints.CAMPAIGN_CODE, { params: { code, channel: chanelId } }),
    )
    .then((response) => {
      return response.data;
    });
}

export function issuePolicyApplication(policyApplication: IPolicyApplication) {
  const card = policyApplication.card;
  const reuseAddress = policyApplication.reuseCustomerAddress;

  const billingDetails = {
    name: get(card, 'billing.name', ''),
    email: get(card, 'billing.email', ''),
    phone: get(card, 'billing.phone', ''),
    address: {
      postcode: get(card, 'billing.address.postcode', ''),
      line1: get(card, 'billing.address.line1', ''),
      line2: get(card, 'billing.address.line2', ''),
      town: get(card, 'billing.address.town', ''),
      county: get(card, 'billing.address.county', ''),
    }
  }

  return axios
    .post(
      url(Endpoints.POLICY_APPLICATION_ISSUE, { params: { id: policyApplication.id } }),
      {
        meta: {
          sms_send: policyApplication.sendSms,
          purchase_declaration: policyApplication.purchaseDeclaration,
          payment_token: policyApplication.paymentToken,
          payment_type: policyApplication.payOnAccount ? 'account' : 'card',
          billing: reuseAddress ? {
            address: policyApplication.travellers[0].address
          } : billingDetails,
        },
        data: {
          relationships: {
            scheme: {
              data: {
                id: policyApplication.schemeId,
              },
            },
          },
        },
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data;
    });
}

export function listDestinations(): Promise<IDestination[]> {
  // Absurdly high limit to get pass limitations in pagination
  return axios
    .get(url(Endpoints.DESTINATIONS, { limit: 10000 }))
    .then((response) => {
      return destinations.fromJsonApi(response.data.data);
    });
}

export function getChannel(key: string): Promise<IChannel> {
  return axios
    .post(url(Endpoints.CHANNEL, { params: { key }, includes: ['products', 'products.schemes.documents'] }))
    .then((response) => {
      return channels.fromJsonApi(response.data.data, response.data.included);
    });
}

export function startScreeningCega(id?: string): Promise<string> {
  return axios
    .post(
      url(Endpoints.SCREENING_START_CEGA),
      id ? { meta: { id } } : null,
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data.meta;
    });
}

export function finishScreeningCega(id: string): Promise<any> {
  return axios
    .post(
      url(Endpoints.SCREENING_FINISH_CEGA),
      { meta: { id } },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data;
    });
}

export function lookupPostcode(postcode: string): Promise<IPostcodeLookup[]> {
  return axios
    .get(
      url(Endpoints.POSTCODE_LOOKUP, { params: { postcode: encodeURIComponent(postcode) } }),
    )
    .then((response) => {
      return response.data.data.map((lookup) => ({
        id: lookup.id,
        line: lookup.attributes.name,
      }));
    });
}

export function getPostcode(id: string): Promise<IPostcodeAddress> {
  return axios
    .get(
      url(Endpoints.POSTCODE_GET, { params: { id } }),
    )
    .then((response) => {
      return response.data.data.attributes;
    });
}

export function getPaymentUrl(transactionId: string) {
  return axios.defaults.baseURL + '/' + url(Endpoints.TRANSACTION_PAY, {
    params: {
      id: transactionId,
    },
  });
}

export function showDestinations(tripType: TripType): boolean {
  return tripType === 'single';
}

export function showRegions(tripType: TripType): boolean {
  return tripType === 'annual' || tripType === 'longstay';
}

export function documentsPoll(documentsPollToken: string) {
  return axios
    .post(
      url(Endpoints.POLICY_DOCUMENTS_POLL),
      { documentsPollToken },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return response.data;
    });
}

export function getCustomer() {
  return axios
    .get(
      url(Endpoints.CUSTOMER, {
        includes: [
          'policies.documents',
          'policies.premiums',
          'quotes.documents',
          'quotes.brokers',
          'renewals.documents',
          'renewals_lapsed_by_customer.documents',
        ],
      }),
    )
    .then((response) => {
      return customer.fromJsonApi(response.data.data, response.data.included);
    });
}

export function updateCustomer(customerValues: ICustomer) {
  return axios
    .patch(
      url(Endpoints.CUSTOMER, {
        includes: ['policies.documents', 'policies.premiums',
          'quotes.documents', 'quotes.premiums'],
      }),
      customer.toJsonApi(customerValues),
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    )
    .then((response) => {
      return customer.fromJsonApi(response.data.data, response.data.included);
    });
}

export function passwordReminder(email, requestItems, scheme, host) {
  return axios
    .post(
      url(Endpoints.FORGOTTEN_PASSWORD),
      { email, requestItems, scheme, host },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
}

export function accountCreator(email, requestItems, scheme, host) {
  return axios
    .post(
      url(Endpoints.FORGOTTEN_PASSWORD),
      { email, requestItems, scheme, host },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
}

// noinspection TsLint
export function resetPassword(token, { password, passwordConfirmation, email, dob }) {
  return axios
    .post(
      url(Endpoints.PASSWORD_RESET),
      {
        token,
        password,
        password_confirmation: passwordConfirmation,
        dob: moment(dob, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        email,
      },
      { headers: { 'Content-Type': 'application/vnd.api+json' } },
    );
}

export function isGlobalPayOrigin(origin){
  try {
    const { hostname, protocol } = new URL(origin);
    if (!/^https?:$/.test(protocol)) return false;
    return hostname === 'realexpayments.com' || hostname.endsWith('.realexpayments.com');
  } catch {
    return false;
  }
}

